import { useQuery, useMutation } from 'convex/react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Mail, Calendar, CheckCircle, XCircle, Clock, Edit2, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

const MentorInviteInfo = () => {
  const navigate = useNavigate();
  const { device } = useAuth();
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  
  // Get the user's email from the device context
  const userEmail = device?.user?.email || '';
  const deviceCode = device?.code || '';

  const invitations = useQuery(
    api.users.invite.getInvitationsByInvitorEmail,
    userEmail && deviceCode ? { invitorEmail: userEmail, deviceCode } : 'skip'
  );

  const updateEmail = useMutation(api.users.invite.updateInvitationEmail);

  const isLoading = invitations === undefined;

  const handleEdit = (invitationId: string, currentEmail: string) => {
    setEditingId(invitationId);
    setNewEmail(currentEmail);
  };

  const handleCancel = () => {
    setEditingId(null);
    setNewEmail('');
  };

  const handleSave = async (invitationId: any) => {
    if (!newEmail || !userEmail || !deviceCode) return;

    try {
      const result = await updateEmail({
        invitationId,
        invitorEmail: userEmail,
        deviceCode,
        newEmail
      });

      if (result.status === 'success') {
        toast({
          title: 'Success',
          description: 'Invitation email updated successfully'
        });
        setEditingId(null);
        setNewEmail('');
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update invitation email',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/login')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Mentor Invite Information</h1>
          <p className="text-muted-foreground">View and manage your mentor invitations</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading invitations...</p>
            </div>
          </div>
        ) : invitations && invitations.length > 0 ? (
          <div className="grid gap-4">
            {invitations.map((invitation) => (
              <Card key={invitation.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Invitation to</p>
                      <CardTitle className="text-lg">
                        {invitation.invitorFirstName} {invitation.invitorLastName}
                      </CardTitle>
                    </div>
                    {invitation.isUsed ? (
                      <Badge variant="default" className="bg-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Used
                      </Badge>
                    ) : invitation.isExpired ? (
                      <Badge variant="destructive">
                        <XCircle className="w-3 h-3 mr-1" />
                        Expired
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Mail className="w-4 h-4 mr-2" />
                    {editingId === invitation.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="max-w-xs h-8"
                          placeholder="Enter new email"
                        />
                        <Button size="sm" onClick={() => handleSave(invitation.id)}>
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={handleCancel}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>{invitation.email}</span>
                        {!invitation.isUsed && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => handleEdit(invitation.id, invitation.email)}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Expires: {format(new Date(invitation.expiresAt), 'PPP')}</span>
                  </div>

                  {invitation.isUsed && invitation.usedAt && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span>Used on: {format(new Date(invitation.usedAt), 'PPP')}</span>
                    </div>
                  )}

                  {invitation.device && (
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium">Device ID:</span> {invitation.device}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Invitations Found</h3>
              <p className="text-muted-foreground">
                You haven't sent any mentor invitations yet.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MentorInviteInfo;
